import { Table } from '@mui/material';

import RefreshIcon from '../../../assets/svg/RefreshIcon';
import { useTableSort } from '../../../hooks';
import { Button, Loader, TableHead } from '../../Shared';
import { getFormattedReports } from './helper';
import { TableBody } from './TableBody';

const STATE = {
  HAS_DATA: 'hasData',
  HAS_NO_DATA: 'hasNoData',
  IS_DATA_LOADING: 'isDataLoading',
  IS_DATA_ERROR: 'isDataError',
};

const getState = (data, isDataLoading, isDataError) => {
  if (isDataLoading) return STATE.IS_DATA_LOADING;
  if (isDataError) return STATE.IS_DATA_ERROR;
  const hasNoData = data.length === 0;
  if (hasNoData) return STATE.HAS_NO_DATA;
  return STATE.HAS_DATA;
};

const getCells = (isAdmin, isManager, isPrecisionMeetingEnabled) => [
  ...(isAdmin
    ? [{ id: 'tenantName', label: 'Client Name', sortable: true }]
    : []),
  ...(isAdmin || isManager
    ? [{ id: 'fullName', label: 'Caller Name', sortable: true }]
    : []),
  { id: 'createdAt', label: 'Booked On', sortable: true },
  { id: 'scheduledDate', label: 'Scheduled', sortable: true },
  { id: 'rescheduledDate', label: 'Rescheduled', sortable: true },
  ...(!isAdmin ? [{ id: 'apptType', label: 'Appt Type', sortable: true }] : []),
  { id: 'companyName', label: 'Company Name', sortable: true },
  { id: 'contactName', label: 'Contact Name', sortable: true },
  { id: 'contactTitle', label: 'Contact Title', sortable: true },
  { id: 'phoneNumber', label: 'Reached Phone Number', sortable: false },
  { id: 'email', label: 'Email', sortable: false },
  { id: 'status', label: 'Status', sortable: false },
  {
    id: 'actions',
    label: '',
    width: isPrecisionMeetingEnabled ? '152px' : '104px',
    sortable: false,
  },
];

export const ReportTable = ({
  isAdmin,
  isManager,
  reports,
  selectedApptIDs,
  isReportsLoading,
  isReportsError,
  fetchReports,
  onChangeSelectedApptIDs,
  openActivityLogModal,
  openRescheduleDemoModal,
  onChangeReports,
  filterByStatuses,
  isPrecisionMeetingEnabled,
}) => {
  const { order, orderBy, onRequestSort, sortedRows } = useTableSort(
    'createdAt',
    getFormattedReports(reports),
  );

  const onSelectAllRows = event => {
    const checked = event.target.checked;

    if (checked) {
      const nextSelectedApptIDs = sortedRows.map(({ apptID }) => apptID);
      onChangeSelectedApptIDs(nextSelectedApptIDs);
      return;
    }

    onChangeSelectedApptIDs([]);
  };

  const onSelectRow = selectedApptID => {
    onChangeSelectedApptIDs(prev => {
      const isSelected = prev.includes(selectedApptID);

      if (isSelected) {
        const nextSelectedApptIDs = prev.filter(
          ApptID => ApptID !== selectedApptID,
        );

        return nextSelectedApptIDs;
      }

      return [...prev, selectedApptID];
    });
  };

  const isSomeRowsSelected =
    selectedApptIDs.length > 0 && selectedApptIDs.length < sortedRows.length;
  const isAllRowsSelected =
    sortedRows.length > 0 && selectedApptIDs.length === sortedRows.length;
  const state = getState(sortedRows, isReportsLoading, isReportsError);

  return (
    <>
      {(state === STATE.HAS_DATA || state === STATE.HAS_NO_DATA) && (
        <div className="overflow-x-auto">
          <Table stickyHeader>
            <TableHead
              isCheckboxVisible={isAdmin || isManager}
              cells={getCells(isAdmin, isManager, isPrecisionMeetingEnabled)}
              isSomeRowsSelected={isSomeRowsSelected}
              isAllRowsSelected={isAllRowsSelected}
              onSelectAllRows={onSelectAllRows}
              order={order}
              orderBy={orderBy}
              onRequestSort={onRequestSort}
            />
            {state === STATE.HAS_DATA && (
              <TableBody
                isAdmin={isAdmin}
                isManager={isManager}
                reports={sortedRows}
                selectedApptIDs={selectedApptIDs}
                onSelectRow={onSelectRow}
                openActivityLogModal={openActivityLogModal}
                openRescheduleDemoModal={openRescheduleDemoModal}
                onChangeReports={onChangeReports}
                filterByStatuses={filterByStatuses}
                isPrecisionMeetingEnabled={isPrecisionMeetingEnabled}
              />
            )}
          </Table>
        </div>
      )}
      {state === STATE.IS_DATA_LOADING && (
        <div className="h-full flex justify-center items-center">
          <Loader />
        </div>
      )}
      {state === STATE.IS_DATA_ERROR && (
        <div className="h-full flex justify-center items-center flex-col gap-3 bg-brand-300/[0.08] rounded-[20px]">
          <div className="flex justify-center items-center flex-col gap-1.5">
            <span className="text-base-1000 text-[22px]/[28px] font-semibold">
              Failed to load records
            </span>
            <span className="text-base-800 text-[16px]/[24px]">
              Error occurred while loading. Try again.
            </span>
          </div>
          <Button
            title="Reload"
            variant="text"
            className="text-destructive-500 !bg-transparent"
            iconStart={<RefreshIcon className="w-5 h-5 text-destructive-500" />}
            onClick={fetchReports}
          />
        </div>
      )}
      {state === STATE.HAS_NO_DATA && (
        <div className="flex justify-center pt-2 px-6 pb-6">
          <span className="text-base-800 text-[16px]/[24px]">
            No available records
          </span>
        </div>
      )}
    </>
  );
};
